import type { CodeToolCallExecutionRequest } from "@repo/app-types/api/interviewLivekitAgent";
import type { CodeToolLogEntry } from "@repo/app-types/helpers/codeToolTurn";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { PH_MASK_CLASS } from "../../analytics/sessionRecordingMasking.js";

interface CodeToolRunDetailsProps {
  requests: readonly CodeToolCallExecutionRequest[];
  logs: readonly CodeToolLogEntry[];
}

/** The requests a code tool's script made and what it logged, in order. */
export const CodeToolRunDetails: FC<CodeToolRunDetailsProps> = (props) => {
  const { requests, logs } = props;

  return (
    <>
      <div>
        <div className="mb-1 font-medium text-muted-foreground">Requests</div>
        {requests.length === 0 ? (
          <p className="text-muted-foreground">(none)</p>
        ) : (
          <ul
            className={twMerge(
              "divide-y divide-border-light rounded bg-gray-50 font-mono text-[11px]",
              PH_MASK_CLASS,
            )}
          >
            {requests.map((request) => (
              <li key={request.seq} className="flex items-baseline gap-2 p-2">
                <span className="font-semibold">{request.method}</span>
                <span className="min-w-0 flex-1 break-all">{request.url}</span>
                <CodeToolRequestOutcome request={request} />
                <span className="shrink-0 text-muted-foreground">
                  {request.duration_ms.toFixed(0)} ms
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="mb-1 font-medium text-muted-foreground">Logs</div>
        {logs.length === 0 ? (
          <p className="text-muted-foreground">(none)</p>
        ) : (
          <ol
            className={twMerge(
              "rounded bg-gray-50 p-2 font-mono text-[11px]",
              PH_MASK_CLASS,
            )}
          >
            {logs.map((entry, index) => (
              <li
                key={index}
                className={twMerge(
                  "break-all whitespace-pre-wrap",
                  entry.level === "warn" && "text-amber-700",
                  entry.level === "error" && "text-red-700",
                )}
              >
                {entry.level === "log" ? "" : `[${entry.level}] `}
                {entry.message}
              </li>
            ))}
          </ol>
        )}
      </div>
    </>
  );
};

interface CodeToolRequestOutcomeProps {
  request: CodeToolCallExecutionRequest;
}

const CodeToolRequestOutcome: FC<CodeToolRequestOutcomeProps> = (props) => {
  const { request } = props;

  if (request.status !== undefined) {
    const isSuccess = request.status >= 200 && request.status < 300;
    return (
      <span
        className={twMerge(
          "shrink-0",
          isSuccess ? "text-green-700" : "text-red-700",
        )}
      >
        {request.status}
      </span>
    );
  }

  return (
    <span className="max-w-[50%] shrink-0 break-words text-red-700">
      {request.error ?? "No response"}
    </span>
  );
};
